import Sortable from 'sortablejs'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'

import { useAutosave } from '@/composables/useAutosave'
import { useToast } from '@/composables/useToast'
import api from '@/store/api'
import { mapRelationship, mapRelationships } from '@/store/mappers'
import { adoptCreatedIds } from '@/view/ai/websiteQuestions'

export const TYPE_OPTIONS = [
  { name: 'Short text', value: 'short_text' },
  { name: 'Long text', value: 'long_text' },
  { name: 'Number', value: 'number' },
  { name: 'Single select', value: 'single_select' },
  { name: 'Multi select', value: 'multi_select' },
  { name: 'Date', value: 'date' }
]

export const SELECT_TYPES = ['single_select', 'multi_select']

// The strings the merchant edits by clicking them inside the preview.
export const EDITABLE_TEXT = ['title', 'description', 'submit_button_text', 'chat_title', 'chat_subtitle']

export const isSelect = field => SELECT_TYPES.includes(field.field_type)
export const typeLabel = value => TYPE_OPTIONS.find(option => option.value === value)?.name || value

let nextKey = 0

const buildField = attributes => ({
  key: `field-${nextKey++}`,
  id: attributes?.id || null,
  label: attributes?.label || '',
  purpose: attributes?.purpose || '',
  field_type: attributes?.field_type || 'short_text',
  required: attributes?.required ?? false,
  // What the answer says about the person rather than about their enquiry. Kept
  // on the field so renaming a label cannot quietly unwire it.
  maps_to: attributes?.maps_to || null,
  options: [...(attributes?.options || [])]
})

// An embed is edited the same way whoever owns it: the same wording, colours,
// questions and key, saved the same way. Only the path it lives at differs, so
// that is the only thing this takes.
//
// `path` is a string, or a function returning one, so a route-scoped owner can
// resolve its id at call time.
export function useWebsiteEmbed(path) {
  const resource = () => (typeof path === 'function' ? path() : path)

  const { showToast } = useToast()

  const websiteId = ref(null)
  const fields = ref([])
  const removedIds = ref([])
  const editingKey = ref(null)
  const saveError = ref('')
  // The uploader creates the attachment and hands us what changed; claiming it
  // is the server's job, so it rides along with the next save.
  const logoChange = ref(null)
  const uploaderKey = ref(0)
  const rows = ref(null)
  const questionList = ref(null)

  const form = reactive({
    enabled: false,
    chat_enabled: false,
    collect_email: false,
    show_logo: false,
    default_membership_tier_id: null,
    title: '',
    description: '',
    submit_button_text: '',
    chat_title: '',
    chat_subtitle: '',
    chat_opening_line: '',
    form_colors: {},
    chat_colors: {},
    form_alignment: {}
  })

  const website = computed(() => (websiteId.value ? mapRelationship({ type: 'website', id: websiteId.value }) : null))
  // Store records nest everything under .attributes; flattening once here keeps
  // the template from having to repeat it.
  const websiteAttrs = computed(() => website.value?.attributes ?? {})
  // The persisted switches, not the ones the merchant may have just flipped. A
  // snippet is only real, and its test page only works, once the server agrees
  // that channel is on.
  const savedFormEnabled = computed(() => websiteAttrs.value.form_live === true)
  const savedChatEnabled = computed(() => websiteAttrs.value.chat_live === true)
  const colorDefaults = computed(() => websiteAttrs.value.color_defaults ?? { form: {}, chat: {} })
  const wordingDefaults = computed(() => websiteAttrs.value.wording_defaults ?? {})
  const channels = computed(() => websiteAttrs.value.channels ?? ['form'])
  // Whether the field list is the merchant's to change. The server decides; the
  // view renders a read-only list off it.
  const lockedQuestions = computed(() => websiteAttrs.value.locked_questions === true)

  const logoFiles = computed(() => {
    const logo = mapRelationship(website.value?.relationships?.logo?.data)
    return logo ? [logo] : []
  })

  const tierOptions = computed(() => [
    { name: 'No tier', value: null },
    ...(websiteAttrs.value.eligible_membership_tiers ?? []).map(tier => ({ name: tier.name, value: tier.id }))
  ])

  // A field the merchant has switched off keeps its row and its place in the
  // order, but a visitor never sees it - so neither does the preview.
  const shownFields = computed(() => fields.value.filter(field => form.collect_email || field.maps_to !== 'email'))

  const previewQuestions = computed(() =>
    shownFields.value.map(field => ({
      id: field.id || field.key,
      label: field.label,
      field_type: field.field_type,
      required: field.required,
      options: isSelect(field) ? field.options.filter(option => option.trim()) : []
    }))
  )

  const sharedPreview = computed(() => ({
    questions: previewQuestions.value,
    consent_text: websiteAttrs.value.consent_text,
    success_message: websiteAttrs.value.success_message,
    default_country: websiteAttrs.value.default_country,
    countries: websiteAttrs.value.countries ?? []
  }))

  // What the form would show right now, switch included, so ticking the logo on
  // paints it without a save. A freshly uploaded one still needs saving first -
  // until then the server has nothing to serve.
  const previewLogoUrl = computed(() => {
    if (!form.show_logo) return null

    const storefront = websiteAttrs.value.storefront_logo_url || null
    if (logoChange.value?._destroy) return storefront
    return websiteAttrs.value.logo_url || storefront
  })

  const formPreview = computed(() => ({
    ...sharedPreview.value,
    logo_url: previewLogoUrl.value,
    title: form.title,
    description: form.description,
    submit_button_text: form.submit_button_text,
    colors: form.form_colors,
    alignment: form.form_alignment
  }))

  const chatPreview = computed(() => ({
    ...sharedPreview.value,
    chat_title: form.chat_title,
    chat_subtitle: form.chat_subtitle,
    chat_opening_line: form.chat_opening_line,
    colors: form.chat_colors
  }))

  const applyEdit = (field, value) => {
    if (EDITABLE_TEXT.includes(field)) form[field] = value
  }

  const addField = () => {
    const field = buildField()
    fields.value.push(field)
    editingKey.value = field.key
    nextTick(() => {
      const editor = questionList.value?.querySelector('[data-question-editor]')
      editor?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
      editor?.querySelector('input')?.focus({ preventScroll: true })
    })
  }

  // accepts_nested_attributes_for only updates or creates what it is sent, so a
  // question left out of the payload is not deleted - it just comes back on the
  // next load. Deletions have to be sent as _destroy, which means remembering the
  // ids that are no longer on screen.
  const removeField = index => {
    const [removed] = fields.value.splice(index, 1)
    if (removed.id) removedIds.value.push(removed.id)
    if (editingKey.value === removed.key) editingKey.value = null
  }

  // api.call resolves to { data: <the JSONAPI document> }, so the record itself
  // is one level deeper than it looks.
  const load = () =>
    api.call('GET', `${resource()}?included=website_questions`).then(response => {
      const record = response?.data?.data
      if (!record) return

      const attributes = record.attributes ?? {}
      const defaults = attributes.color_defaults ?? { form: {}, chat: {} }

      websiteId.value = record.id
      Object.assign(form, {
        enabled: attributes.enabled ?? false,
        chat_enabled: attributes.chat_enabled ?? false,
        collect_email: attributes.collect_email ?? false,
        show_logo: attributes.show_logo ?? false,
        default_membership_tier_id: attributes.default_membership_tier_id ?? null,
        title: attributes.title ?? '',
        description: attributes.description ?? '',
        submit_button_text: attributes.submit_button_text ?? '',
        chat_title: attributes.chat_title ?? '',
        chat_subtitle: attributes.chat_subtitle ?? '',
        chat_opening_line: attributes.chat_opening_line ?? '',
        form_colors: { ...defaults.form, ...attributes.form_colors },
        chat_colors: { ...defaults.chat, ...attributes.chat_colors },
        form_alignment: { ...attributes.form_alignment }
      })
      removedIds.value = []
      logoChange.value = null
      fields.value = mapRelationships(record.relationships?.website_questions?.data || []).map(related =>
        buildField({ ...related.attributes, id: related.id })
      )
    })

  const payload = questions => ({
    website: {
      enabled: form.enabled,
      chat_enabled: form.chat_enabled,
      collect_email: form.collect_email,
      show_logo: form.show_logo,
      default_membership_tier_id: form.default_membership_tier_id,
      ...(logoChange.value && { logo_attributes: logoChange.value }),
      title: form.title,
      description: form.description,
      submit_button_text: form.submit_button_text,
      chat_title: form.chat_title,
      chat_subtitle: form.chat_subtitle,
      chat_opening_line: form.chat_opening_line,
      form_colors: form.form_colors,
      chat_colors: form.chat_colors,
      form_alignment: form.form_alignment,
      website_questions_attributes: [
        ...questions.map(({ field, position }) => ({
          ...(field.id ? { id: field.id } : {}),
          label: field.label,
          purpose: field.purpose,
          field_type: field.field_type,
          required: field.required,
          maps_to: field.maps_to,
          position,
          options: isSelect(field) ? field.options.filter(option => option.trim()) : []
        })),
        ...removedIds.value.map(id => ({ id, _destroy: true }))
      ]
    }
  })

  const isComplete = field =>
    Boolean(field.label.trim()) && (!isSelect(field) || field.options.some(option => option.trim()))

  // Positions come from the whole list, so a question held back until it is
  // complete keeps its place instead of shuffling the ones after it.
  const savableQuestions = () =>
    fields.value.map((field, index) => ({ field, position: index + 1 })).filter(({ field }) => isComplete(field))

  const fingerprint = () => JSON.stringify(payload(savableQuestions()))

  const persist = () => {
    const questions = savableQuestions()
    const body = payload(questions)
    const sent = JSON.stringify(body)
    const removed = [...removedIds.value]
    const sentLogo = logoChange.value

    return api.call('PUT', `${resource()}?included=website_questions`, body).then(response => {
      const untouched = fingerprint() === sent
      const known = [...fields.value.map(field => field.id), ...removedIds.value]
      const matched = adoptCreatedIds(
        questions.map(({ field }) => field),
        known,
        response?.data
      )
      removedIds.value = removedIds.value.filter(id => !removed.includes(id))
      // Only if nothing was picked while this was in flight, or the newer choice
      // would be thrown away before it saved.
      if (sentLogo && logoChange.value === sentLogo) {
        logoChange.value = null
        uploaderKey.value += 1
      }
      saveError.value = ''
      if (!matched) return load().then(fingerprint)
      return untouched ? fingerprint() : sent
    })
  }

  const { pending: syncing, failed: saveFailed, schedule, flush, reset } = useAutosave({ fingerprint, persist })

  // Flushed first: an in-flight autosave would otherwise land after the server
  // has created the rows and clobber them.
  const addStarterQuestions = () =>
    flush()
      .then(() => api.call('POST', `${resource()}/add_starter_questions`, {}))
      .then(load)
      .then(reset)

  // Watched rather than set from an @input on each control: the questions list is
  // nested and also changes through drag-reorder, add and delete, which no input
  // handler sees.
  watch([form, fields, removedIds, logoChange], schedule, { deep: true })

  watch(saveFailed, error => {
    if (!error) return
    saveError.value = error.response?.data?.errors?.[0] || 'Unable to save right now.'
    showToast(`Your last change didn't save: ${saveError.value}`, { type: 'error' })
  })

  // Reorder in place; position is written from array index on save.
  watch(rows, () => {
    if (!rows.value) return

    Sortable.create(rows.value, {
      handle: '.drag-handle',
      animation: 150,
      onEnd: event => {
        const [moved] = fields.value.splice(event.oldIndex, 1)
        fields.value.splice(event.newIndex, 0, moved)
        nextTick()
      }
    })
  })

  const holdUnload = event => {
    if (!syncing.value) return
    event.preventDefault()
    event.returnValue = ''
  }

  onBeforeRouteLeave(() => flush())
  onMounted(() => window.addEventListener('beforeunload', holdUnload))
  onBeforeUnmount(() => window.removeEventListener('beforeunload', holdUnload))

  return {
    form,
    fields,
    removedIds,
    editingKey,
    rows,
    questionList,
    website,
    websiteAttrs,
    websiteId,
    savedFormEnabled,
    savedChatEnabled,
    colorDefaults,
    wordingDefaults,
    channels,
    lockedQuestions,
    shownFields,
    logoChange,
    logoFiles,
    uploaderKey,
    tierOptions,
    formPreview,
    chatPreview,
    syncing,
    saveError,
    load,
    addStarterQuestions,
    addField,
    removeField,
    applyEdit,
    isComplete,
    flush,
    reset
  }
}
